/**
 * `<slidein-panel>` renders a lazy-built option-picker dialog with stock-status labels.
 * `<selector-slidein>` manages the open button, panel creation/reuse, and variant-change sync.
 */
window._slideInCounter = window._slideInCounter || 0
if (!customElements.get('slidein-panel') && typeof SlideInAbstract === 'function') {

  class ComponentSlideinPanel extends SlideInAbstract {

    constructor() {
      super()
    }

    connectedCallback() {
      super.connectedCallback()
      this.inStockLabel = this.dataset.inStockLabel;
      this.lastLabel = this.dataset.lastLabel;
      this.lastThreshold = this.dataset.lastThreshold ? parseInt(this.dataset.lastThreshold) : 0;
    }

    setOptions(options) {
      const container = this.querySelector('.items');

      // Preserve focused radio value before the DOM is wiped by replaceChildren().
      // This is needed because a variant change triggers a full rebuild while the
      // user may still be navigating with the keyboard.
      const focusedValue = container.contains(document.activeElement)
        ? document.activeElement.value
        : null;

      container.replaceChildren();

      const list = document.createElement('ul');
      const groupName = `slidein-${this.id}`;
      const self = this;

      options.forEach((opt, index) => {

        const { label: displayLabel, stock, available } = this.getStockStatus(opt)

        const inputId = `${groupName}-${index}`;
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = groupName;
        input.id = inputId;
        input.value = opt.value;
        input.checked = opt.value === this._selectedValue;
        input.dataset.stock = stock;

        input.addEventListener('change', () => {
          const closePanel = self._lastSelectWasPointer;
          self._lastSelectWasPointer = false;

          self._selectedValue = opt.value;
          self.dispatchEvent(new CustomEvent('slideInSelector:changed', { detail: { value: opt.value, label: opt.label } }));

          if (closePanel) self.hide();
        });

        const label = document.createElement('label');
        label.htmlFor = inputId;
        label.innerHTML = `<span>${opt.label}</span>`;
        label.classList.add('opt-slide-in', 'flex', 'align-items-center', 'justify-content-between');
        label.style.setProperty('--label-text', `"${displayLabel}"`);


        label.addEventListener('pointerdown', () => {
          self._lastSelectWasPointer = true;
        });

        const li = document.createElement('li');
        li.appendChild(input);
        li.appendChild(label);
        list.appendChild(li);
      }, this);

      container.appendChild(list);

      if (focusedValue) {
        this.querySelector(`input[type="radio"][value="${focusedValue}"]`)?.focus();
      }
    }

    getStockStatus(opt) {
      let stock = 'no';
      let label = '';
      let available = false;
      if (opt.available && opt.inventory_quantity >= this.lastThreshold && this.inStockLabel) {
        stock = 'stock'
        label = this.inStockLabel
        available = true;
      } else if (opt.available && opt.inventory_quantity > 0 && opt.inventory_quantity < this.lastThreshold) {
        stock = 'low'
        label = this.lastLabel.replace('[COUNT]', opt.inventory_quantity)
        available = true;
      }
      return {
        stock,
        label,
        available
      }
    }

    setStatus(filtered) {
      if (filtered) {
        this.classList.add('in-stock')
        this.querySelectorAll('.items input[type="radio"]').forEach(el => el.disabled = el.dataset.stock == 'no' )
      } else {
        this.classList.remove('in-stock')
        this.querySelectorAll('.items input[type="radio"]').forEach(el => el.disabled = false )
      }
    }

    setSelectedOption(value) {
      this._selectedValue = value;
      const radio = this.querySelector(`input[type="radio"][value="${value}"]`);
      if (radio) radio.checked = true;
    }
  }

  customElements.define('slidein-panel', ComponentSlideinPanel);
}

if (!customElements.get('selector-slidein')) {

  class ComponentSelectorSlidein extends HTMLElement {
    constructor() {

      super();
      this._uniqueId = ++window._slideInCounter;

      this.label = this.dataset.label;
      this.isHighVariants = this.dataset.isHighVariants === 'true';
      this.openButton = this.querySelector('.btn-open');

      this.openButtonHandler = this.onOpen.bind(this)
      this.slideInChangedHandler = this.onSlideInChanged.bind(this);
      this.variantChangeHandler = this.onVariantChange.bind(this)

      this.mainElement = this.closest('.product-main-element')

      this.init();
    }

    connectedCallback() {
      this.openButton.addEventListener('click', this.openButtonHandler);
      this.mainElement?.addEventListener('variantChange', this.variantChangeHandler);

      if (this.mainElement) {
        this.stockFilterHandler = this.onStockFilterUpdate.bind(this)
        this.mainElement.addEventListener('inStockFilter:update', this.stockFilterHandler)
      }
    }

    disconnectedCallback() {
      this.openButton.removeEventListener('click', this.openButtonHandler);
      this.mainElement?.removeEventListener('variantChange', this.variantChangeHandler);
      this.mainElement?.removeEventListener('inStockFilter:update', this.stockFilterHandler)
      this.panel?.addEventListener('slideInSelector:changed', this.slideInChangedHandler);
    }


    init() {
      this.panel = this.getOrCreatePanel();
      this.refreshOptions()

      if (this.mainElement && this.mainElement.getStockFilterState) {
        this.panel.setStatus(this.mainElement.getStockFilterState())
      }

      this.update(this.mainElement.getCurrentVariant())

    }

    onStockFilterUpdate(event) {
      if (!this.panel) return;
      this.panel.setStatus(event.detail.filtered)
    }

    // Event that is fired every time a user selects an option from the slide-in panel
    // Works for both HV && !HV
    onSlideInChanged(event) {

      const { select } = this.getSelector()
      select.value = event.detail.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.update()
    }

    // Only works for HV
    onVariantChange(event) {
      this.refreshOptions()
      this.update(event.detail.variant)
    }

    onOpen(event) {
      event.preventDefault();
      this.panel?.show();
    }

    getSelector () {
      return {
        element: this.previousElementSibling,
        select: this.previousElementSibling.querySelector('select')
      };
    }

    update(selectedVariant) {
      if (!selectedVariant) return;

      // updating label
      const {
        element,
        select,
      } = this.getSelector();
      if (!select) return;

      const optionIndex = parseInt(element.dataset.option)

      let selectedValue, displayLabel;
      if (this.isHighVariants) {

        selectedValue = select.value;
        displayLabel = selectedVariant.options[optionIndex - 1]
      } else {
        selectedValue = selectedVariant.options[optionIndex - 1]
        displayLabel = selectedVariant.options[optionIndex - 1]
      }

      this.panel.setSelectedOption(selectedValue);
      this.openButton.querySelector('span').textContent = this.label.replace('[VALUE]', displayLabel)
    }

    refreshOptions() {

      const {
        element,
        select
      } = this.getSelector();
      if (!select) return;

      const optionIndex = parseInt(element.dataset.option)

      // Building availability for allVariants
      let availableOptions = []
      if (!this.isHighVariants) {
        const availabilityData = this.getAvailability(
          this.mainElement.productData,
          this.mainElement.getCurrentVariant(),
          this.mainElement.productData.options_with_values.map(option => ({ name: option.name, position: option.position }))
        )
        availableOptions = availabilityData.find(optionData => optionData.option.position === optionIndex)
      }

      const options = Array.from(select.querySelectorAll('option')).map((option) => {
        const availability = this.isHighVariants ?
          {
            available: option.dataset.available == 'true',
            inventory_quantity: parseInt(option.dataset.inventoryQuantity)
          } : availableOptions
            .availability
            .find(dataOption => option.value === dataOption.value)
        return {
            label: option.textContent.trim(),
            value: option.value,
            ...availability
          };
      });

      this.panel?.setOptions(options);
    }

    getOrCreatePanel() {

      let panelId = `${$s.handleize(this.label)}-modal`
      let panel = document.getElementById(panelId);

      if (!panel) {

        const optionName = this.dataset.optionName || '';
        const mainScheme = this.dataset.modalsMainColorScheme || '';
        const headingScheme = this.dataset.modalsHeadingColorScheme || '';
        const headingBorder = this.dataset.modalsHeadingBorder === 'true';
        const iconTemplate = this.querySelector('template');
        const closeIconHtml = iconTemplate ? iconTemplate.innerHTML : '';

        panel = document.createElement('slidein-panel');
        panel.id = panelId;
        panel.dataset.inStockLabel = this.dataset.inStockLabel;
        panel.dataset.lastLabel = this.dataset.lastLabel;
        panel.dataset.lastThreshold = this.dataset.lastThreshold;

        panel.classList.add('ease-animation', 'slide-in-modal', 'slide-in-selector', 'inner-scroll', mainScheme);
        panel.role = 'dialog';
        panel.ariaModal = 'true';

        panel.innerHTML = [
          '<div class="content scheme flex direction-column">',
          '<div class="head scheme color-' + headingScheme + ' ' + (headingBorder ? 'scheme-border-bottom' : '') + '">',
          '<div class="title flex align-items-center">' + optionName + '</div>',
          '<button class="close">' + closeIconHtml + '</button>',
          '</div>',
          '<div class="wrap flex-1 items"></div>',
          '</div>'
        ].join('');

        document.body.appendChild(panel);
      }

      panel.addEventListener('slideInSelector:changed', this.slideInChangedHandler);

      return panel;
    }

    getAvailability(product, selectedVariant, productOptions) {
      if (productOptions.length <= 1) return [];

      const variants = product.variants;
      const lastIdx = productOptions.length - 1;
      const lastOption = productOptions[lastIdx];

      // Anchor all preceding options to the selected variant's values.
      let pool = variants;
      for (let i = 0; i < lastIdx; i++) {
        const anchor = selectedVariant.options[i];
        if (anchor != null) {
          pool = pool.filter((v) => v.options[i] === anchor);
        }
      }

      // Collect distinct last-option values in first-occurrence order.
      // Each anchored combination maps to exactly one variant, so we
      // just read available / inventory_quantity straight from it.
      const seen = new Map();
      for (const v of pool) {
        const val = v.options[lastIdx];
        if (seen.has(val)) {
          // Aggregate in case the data ever has multiple variants per combo.
          const entry = seen.get(val);
          if (v.available) entry.available = true;
          entry.inventory_quantity += v.inventory_quantity ?? 0;
        } else {
          seen.set(val, {
            value: val,
            available: v.available,
            inventory_quantity: v.inventory_quantity ?? 0,
          });
        }
      }

      return [
        {
          option: lastOption,
          availability: [...seen.values()],
        },
      ];
    }

  }

  customElements.define('selector-slidein', ComponentSelectorSlidein);
}
